/**
 * Landing Page FAQ Custom Element
 * Extends the native details/summary pattern with smooth animations
 */
class LandingPageFaq extends HTMLElement {
    /** @type {NodeListOf<HTMLDetailsElement>} */
    get faqItems() {
      return this.querySelectorAll('.faq-item');
    }
  
    #controller = new AbortController();
  
    connectedCallback() {
      const { signal } = this.#controller;
  
      // Add event listeners to all FAQ items
      this.faqItems.forEach(item => {
        const summary = item.querySelector('summary');
        if (summary) {
          summary.addEventListener('click', (e) => this.#handleFaqClick(e, item), { signal });
        }
      });
  
      // Handle escape key for accessibility
      this.addEventListener('keydown', this.#handleKeyDown, { signal });
    }
  
    disconnectedCallback() {
      this.#controller.abort();
    }
  
    /**
     * Handle FAQ item click with smooth scrolling
     * @param {Event} event - Click event
     * @param {HTMLDetailsElement} faqItem - The FAQ item that was clicked
     */
    #handleFaqClick = (event, faqItem) => {
      const wasOpen = faqItem.open;
      
      // If opening an item, scroll it into view after animation starts
      if (!wasOpen) {
        // Small delay to let the animation start
        setTimeout(() => {
          const rect = faqItem.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const offset = 100; // Space from top of viewport
          
          // Only scroll if the FAQ item is not fully visible
          if (rect.top < offset || rect.bottom > viewportHeight - offset) {
            const y = faqItem.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ 
              top: y, 
              behavior: 'smooth' 
            });
          }
        }, 50);
      }
    };
  
    /**
     * Handle keyboard events for accessibility
     * @param {KeyboardEvent} event - Keyboard event
     */
    #handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Find the currently focused element and check if it's within an open details
        const activeElement = document.activeElement;
        const openDetailsElement = activeElement?.closest('details[open]');
        
        if (openDetailsElement && this.contains(openDetailsElement)) {
          // Close only the active/open details element and return focus to its summary
          /** @type {HTMLDetailsElement} */ (openDetailsElement).open = false;
          const summary = openDetailsElement.querySelector('summary');
          if (summary) {
            summary.focus();
          }
          event.preventDefault();
        }
      }
    };
  }
  
  // Register the custom element
  if (!customElements.get('landing-page-faq')) {
    customElements.define('landing-page-faq', LandingPageFaq);
  }
  